import { Outlet, NavLink } from 'react-router-dom'
import { useAuth } from '../hooks/useAuth'
import { useAppStore } from '../stores/app'
import { useBranding } from './BrandingProvider'
import { 
  LayoutDashboard, 
  MessageSquare,
  Users, 
  FileText, 
  Image, 
  DollarSign, 
  Settings,
  LayoutTemplate,
  Cpu,
  LogOut,
  Menu,
  Shield,
  CreditCard,
  ShieldCheck
} from 'lucide-react'
import { useState } from 'react'

const navigation = [
  { name: 'Dashboard', href: '/dashboard', icon: LayoutDashboard },
  { name: 'Chat', href: '/chat', icon: MessageSquare },
  { name: 'Personas', href: '/personas', icon: Users },
  { name: 'Contenido', href: '/content', icon: FileText },
  { name: 'Visual', href: '/visual', icon: Image },
  { name: 'Plantillas', href: '/templates', icon: LayoutTemplate },
  { name: 'Costos', href: '/costs', icon: DollarSign },
  { name: 'Facturacion', href: '/billing', icon: CreditCard },
  { name: 'Configuracion IA', href: '/ai-config', icon: Cpu },
  { name: 'Configuración', href: '/settings', icon: Settings },
]

export function Layout() {
  const { user, signOut } = useAuth()
  const currentUser = useAppStore((state) => state.currentUser)
  const isAdmin = currentUser?.role === 'admin'
  const isSuperAdmin = currentUser?.is_super_admin === true
  const [sidebarOpen, setSidebarOpen] = useState(true)
  const { logoUrl, primaryColor, orgName } = useBranding()

  return (
    <div className="h-screen bg-slate-50 flex overflow-hidden">
      {/* Sidebar */}
      <aside className={`${sidebarOpen ? 'w-64' : 'w-20'} flex-shrink-0 bg-white border-r border-slate-200 transition-all duration-300 print:hidden`}>
        <div className="flex flex-col h-full">
          {/* Logo */}
          <div className="h-16 flex items-center justify-between px-4 border-b border-slate-200">
            {sidebarOpen && (
              logoUrl
                ? (
                  <img
                    src={logoUrl}
                    alt={orgName}
                    className="h-8 w-auto max-w-[140px] object-contain"
                  />
                )
                : (
                  <span
                    className="text-xl font-bold truncate"
                    style={{ color: primaryColor }}
                  >
                    {orgName}
                  </span>
                )
            )}
            <button
              onClick={() => setSidebarOpen(!sidebarOpen)}
              className="p-2 hover:bg-slate-100 rounded-lg flex-shrink-0"
            >
              <Menu className="w-5 h-5 text-slate-600" />
            </button>
          </div>

          {/* Navigation */}
          <nav className="flex-1 py-4 px-2 space-y-1">
            {navigation.map((item) => {
              const isSettings = item.href === '/settings'
              const showAdminIndicator = isSettings && isAdmin
              return (
                <NavLink
                  key={item.name}
                  to={item.href}
                  className={({ isActive }) =>
                    `flex items-center gap-3 px-3 py-2 rounded-lg transition-colors ${
                      isActive
                        ? 'text-slate-900'
                        : 'text-slate-600 hover:bg-slate-100 hover:text-slate-900'
                    }`
                  }
                  style={({ isActive }) =>
                    isActive
                      ? { backgroundColor: `${primaryColor}18`, color: primaryColor }
                      : {}
                  }
                >
                  <item.icon className="w-5 h-5 flex-shrink-0" />
                  {sidebarOpen && (
                    <>
                      <span className="flex-1 min-w-0">{item.name}</span>
                      {showAdminIndicator && (
                        <span title="Incluye opciones de administrador"><Shield className="w-4 h-4 flex-shrink-0 text-amber-500" /></span>
                      )}
                    </>
                  )}
                </NavLink>
              )
            })}
          </nav>

          {/* Back-office link (super-admin only) */}
          {isSuperAdmin && (
            <div className="px-2 pb-2">
              <NavLink
                to="/backoffice"
                className={({ isActive }) =>
                  `flex items-center gap-3 px-3 py-2 rounded-lg transition-colors text-xs font-medium ${
                    isActive
                      ? 'bg-emerald-50 text-emerald-700'
                      : 'text-slate-500 hover:bg-slate-100 hover:text-slate-700'
                  }`
                }
              >
                <ShieldCheck className="w-4 h-4 flex-shrink-0" />
                {sidebarOpen && <span>Back-office</span>}
              </NavLink>
            </div>
          )}

          {/* User */}
          <div className="p-4 border-t border-slate-200">
            <div className="flex items-center gap-3">
              <div
                className="w-8 h-8 rounded-full flex items-center justify-center text-white flex-shrink-0"
                style={{ backgroundColor: primaryColor }}
              >
                {user?.email?.[0].toUpperCase()}
              </div>
              {sidebarOpen && (
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium truncate text-slate-700">{user?.email}</p>
                </div>
              )}
              <button
                onClick={() => signOut()}
                className="p-2 hover:bg-slate-100 rounded-lg text-slate-500 hover:text-slate-700"
              >
                <LogOut className="w-4 h-4" />
              </button>
            </div>
          </div>
        </div>
      </aside>

      {/* Main content */}
      <main className="flex-1 min-h-0 overflow-auto">
        <Outlet />
      </main>
    </div>
  )
}
